import React, { useEffect, useMemo, useState } from "react";
import { getInvoiceById } from "../../api/settings.js";
import ModalBasic from "../../components/ModalBasic.jsx";
import moment from "moment";
import { formatValue } from "../../utils/Utils.js";
import { FaRegEye } from "react-icons/fa6";
import { PrimaryButton, SecondaryButton } from "../../components/Buttons.jsx";
import PastOrderModal from "../pastOrders/PastOrderModal.jsx";
import { FaTriangleExclamation, FaRegFilePdf } from "react-icons/fa6";
import Tooltip from "../../components/Tooltip.jsx";

const InvoiceModal = ({ invoiceID, setInvoiceID }) => {
  const [invoice, setInvoice] = useState(null);
  const [charges, setCharges] = useState([]);
  const [viewOrderID, setViewOrderID] = useState(null);

  useEffect(() => {
    if (!invoiceID) {
      setInvoice(null);
      setCharges([]);
      return;
    }
    getInvoiceById(invoiceID).then((res) => {
      setInvoice(res.invoice);
      setCharges(res.charges);
    });
  }, [invoiceID]);

  const friendlyInvoiceID = useMemo(() => {
    if (!invoiceID) return "";
    return invoiceID.substring(invoiceID.length - 8);
  }, [invoiceID]);

  return (
    <ModalBasic
      modalOpen={Boolean(invoiceID)}
      setModalOpen={() => {
        setInvoiceID("");
      }}
      title={`Invoice #${friendlyInvoiceID}`}
      width={"xl"}
    >
      {invoice && (
        <div>
          <div className="flex flex-row justify-between w-full pb-2">
            <div className="text-left">
              <div className="text-slate-400 dark:text-slate-500">Invoice</div>
              <div className="font-semibold text-slate-800 dark:text-slate-100 capitalize">
                <div>{friendlyInvoiceID}</div>
              </div>

              <div className="text-slate-400 dark:text-slate-500">Status</div>
              <div className="font-semibold text-slate-800 dark:text-slate-100 capitalize">
                <div className={"flex gap-1 items-center"}>
                  {invoice.status}
                  {invoice.status === "FAILED" && (
                    <FaTriangleExclamation className={"text-red-500"} />
                  )}
                  {invoice.status === "FAILED" && invoice.errors.length && (
                    <Tooltip size={'lg'} position={"right"}>
                      {invoice.errors.map((error) => (
                        <div>{moment(error.date).format("MM/DD/YYYY")} - {error.message}</div>
                      ))}
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-slate-400 dark:text-slate-500">
                Date Billed
              </div>
              <div className="font-semibold text-slate-800 dark:text-slate-100 capitalize">
                <div>{moment(invoice.processed_at).format("MMMM Do, YYYY")}</div>
              </div>

              <div className={"mt-2"}>
                <PrimaryButton
                  onClick={() => {
                    window.open(invoice.hosted_invoice_pdf, "_blank");
                  }}
                >
                  <div className={"flex items-center gap-2"}>
                    <FaRegFilePdf />
                    Download
                  </div>
                </PrimaryButton>
              </div>
            </div>
          </div>

          {/* table of charges*/}
          <div>
            <table className="table-auto w-full dark:text-slate-300 divide-y divide-slate-200 dark:divide-slate-700">
              <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Date</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Description</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Amount</div>
                  </th>
                </tr>
              </thead>
              {charges.map((charge) => (
                <tbody>
                  <tr key={charge._id}>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      {moment(charge.order.order_completed).format("MMMM Do, YYYY")}
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap flex gap-1 items-center">
                      {charge.order.order_number}
                      <SecondaryButton
                        naked
                        onClick={() => {
                          setViewOrderID(charge.order._id);
                        }}
                      >
                        <FaRegEye />
                      </SecondaryButton>
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      {formatValue(charge.amount)}
                    </td>
                  </tr>
                </tbody>
              ))}
              <tbody>
                <tr>
                  <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Total</div>
                  </td>
                  <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap"></td>
                  <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">
                      {formatValue(invoice.amount)}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
      <PastOrderModal
        setModalOpen={setViewOrderID}
        modalOpen={Boolean(viewOrderID)}
        orderID={viewOrderID}
      />
    </ModalBasic>
  );
};

export default InvoiceModal;
